/*
  pzz-clean-variant-urls.js
  ------------------------------------------------------------------
  Strips the redundant ?variant= param from internal /products/ links
  for SINGLE-variant products (checked via /products/handle.js), so
  card links don't create ?variant= duplicates of the clean product URL.

  Change from the previous version: the storefront trigger was
  `shopify:section:load`, which Horizon does NOT fire for client-side
  section rendering (recommendations, recently-viewed, predictive search,
  morphed grids). Those links were never cleaned. Replaced with a
  debounced MutationObserver that catches any DOM insertion, plus the
  initial DOMContentLoaded pass. `shopify:section:load` is kept only so
  it still runs inside the Theme Editor.

  Variant counts are cached per handle (including failures) so the
  observer's repeat runs never refetch the same product.
*/
(function () {
  'use strict';
  const cache = {};          // handle -> variant count (or null on fetch failure)
  const queue = new Set();   // added subtrees awaiting a scan
  let timer = null;

  async function cleanLink(link) {
    try {
      const url = new URL(link.href, window.location.origin);
      if (!url.searchParams.has('variant')) return;
      const match = url.pathname.match(/^\/products\/([^\/]+)/);
      if (!match) return;
      const handle = match[1];

      if (cache[handle] === undefined) {
        // Cache the PROMISE so concurrent calls for the same handle share one fetch.
        cache[handle] = fetch('/products/' + handle + '.js')
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => (data ? data.variants.length : null))
          .catch(() => null);
      }
      const count = await Promise.resolve(cache[handle]);
      if (count === 1) link.setAttribute('href', url.pathname);
    } catch (e) { /* malformed href — ignore */ }
  }

  // Scan only a single added subtree, not the whole document.
  function cleanWithin(root) {
    if (!root || root.nodeType !== 1) return;
    if (!root.isConnected) return; // detached by a later morph — skip
    if (root.matches && root.matches('a[href*="?variant="]')) cleanLink(root);
    if (root.querySelectorAll) root.querySelectorAll('a[href*="?variant="]').forEach(cleanLink);
  }

  function flush() {
    timer = null;
    const roots = Array.from(queue);
    queue.clear();
    // Drop roots contained by another queued root — avoids scanning the same
    // <a> elements once per ancestor when nested insertions queue together.
    const top = roots.filter((r) =>
      !roots.some((other) => other !== r && other.contains && other.contains(r))
    );
    top.forEach(cleanWithin);
  }
  function schedule() { if (!timer) timer = setTimeout(flush, 250); }

  function initialScan() {
    document.querySelectorAll('a[href*="?variant="]').forEach(cleanLink);
  }
  // Run the first pass at idle, so its fetches don't compete with critical
  // resources. Link-cleaning only matters before a click, so a short delay is fine.
  function scheduleInitial() {
    if ('requestIdleCallback' in window) requestIdleCallback(initialScan, { timeout: 3000 });
    else setTimeout(initialScan, 800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInitial);
  } else {
    scheduleInitial();
  }

  // Theme Editor only — harmless on the live storefront.
  document.addEventListener('shopify:section:load', initialScan);

  // Catch Horizon's dynamically injected sections/morphs, but scan only the
  // nodes actually added, and only after a burst of mutations settles.
  if (document.body) {
    new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const added = mutations[i].addedNodes;
        if (!added) continue;
        for (let j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) queue.add(added[j]);
        }
      }
      if (queue.size) schedule();
    }).observe(document.body, { childList: true, subtree: true });
  }
})();